import React from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, Clock, User, ArrowLeft, ArrowRight, MessageCircle, ShieldCheck, BookOpen } from "lucide-react";
import { blogPosts, contact, popularCountries } from "../mock";
import Seo from "../components/Seo";

export default function BlogPost() {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="text-2xl font-bold text-slate-900 mb-4">Article not found</h1>
        <Link to="/blog" className="text-teal-600 font-semibold">Back to Blog</Link>
      </div>
    );
  }

  const tags = post.tags || [];
  // Related by shared tags, then fill with others
  const byTags = blogPosts.filter((p) => p.slug !== post.slug && (p.tags || []).some((t) => tags.includes(t)));
  const others = blogPosts.filter((p) => p.slug !== post.slug && !byTags.includes(p));
  const related = [...byTags, ...others].slice(0, 3);
  const recommended = [...byTags, ...others].slice(0, 6);
  const linkCountries = post.country
    ? [popularCountries.find((c) => c.slug === post.country), ...popularCountries.filter((c) => c.slug !== post.country)].filter(Boolean).slice(0, 6)
    : popularCountries.slice(0, 6);

  return (
    <>
      <Seo
        title={`${post.title} | ShipMeds Blog`}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.image}
      />

      <div className="relative h-[320px] lg:h-[420px] overflow-hidden">
        <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/50 to-slate-900/30" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-3xl mx-auto px-4 pb-10 w-full text-white">
            <Link to="/blog" className="inline-flex items-center gap-2 text-teal-200 hover:text-white mb-4 text-sm"><ArrowLeft className="w-4 h-4" /> Back to Blog</Link>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold leading-tight mb-4">{post.title}</h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-teal-100">
              <span className="flex items-center gap-1.5"><User className="w-4 h-4" />{post.author}</span>
              <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" />{post.date}</span>
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{post.readTime}</span>
            </div>
          </div>
        </div>
      </div>

      <article className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4 grid lg:grid-cols-3 gap-10">
          {/* Main content */}
          <div className="lg:col-span-2">
            <p className="text-lg text-slate-700 leading-relaxed font-medium mb-6">{post.excerpt}</p>
            {post.body.map((para, i) => (
              <p key={i} className="text-slate-600 leading-relaxed mb-5">{para}</p>
            ))}

            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-6">
                {tags.map((t) => (
                  <Link key={t} to="/blog" className="text-xs font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 px-3 py-1.5 rounded-full capitalize transition-colors">#{t}</Link>
                ))}
              </div>
            )}

            {/* Customs guarantee callout */}
            <div className="mt-8 bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-6 text-white flex flex-col sm:flex-row items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center shrink-0"><ShieldCheck className="w-7 h-7" /></div>
              <div>
                <h3 className="font-bold text-lg mb-1">100% Customs Clearance Guarantee</h3>
                <p className="text-teal-100 text-sm leading-relaxed mb-3">ShipMeds is India's first medicine courier to guarantee destination customs clearance on branded and unbranded Ayurvedic &amp; Homeopathy medicines. Read <Link to="/blog/100-percent-customs-clearance-guarantee-ayurvedic-homeopathy" className="underline font-semibold hover:text-white">how our guarantee works</Link>.</p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/courier-charges" className="bg-white text-teal-700 font-semibold px-5 py-2.5 rounded-lg hover:bg-teal-50 transition-colors text-sm">Get a Quote</Link>
                  <a href={contact.whatsapp} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors text-sm"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
                </div>
              </div>
            </div>

            {/* Recommended reading - interlinking */}
            <div className="mt-10 bg-slate-50 border border-slate-100 rounded-2xl p-6">
              <h3 className="flex items-center gap-2 font-bold text-slate-900 mb-4"><BookOpen className="w-5 h-5 text-teal-600" /> Recommended Reading</h3>
              <ul className="space-y-3">
                {recommended.map((p) => (
                  <li key={p.slug}>
                    <Link to={`/blog/${p.slug}`} className="group flex items-start gap-2 text-slate-700 hover:text-teal-600 transition-colors">
                      <ArrowRight className="w-4 h-4 mt-1 shrink-0 text-teal-500 group-hover:translate-x-1 transition-transform" />
                      <span className="font-medium">{p.title}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Sidebar interlinks */}
          <aside className="lg:sticky lg:top-28 h-fit space-y-6">
            <div className="bg-white border border-slate-100 card-shadow rounded-2xl p-6">
              <h3 className="font-bold text-slate-900 mb-4">Ship to Popular Countries</h3>
              <ul className="space-y-2">
                {linkCountries.map((c) => (
                  <li key={c.slug}>
                    <Link to={`/country/${c.slug}`} className="group flex items-center justify-between text-sm text-slate-600 hover:text-teal-600 transition-colors py-1">
                      <span className="flex items-center gap-2"><img src={`https://flagcdn.com/w40/${c.code}.png`} alt={c.name} className="w-5 h-3.5 object-cover rounded-sm" /> India to {c.name}</span>
                      <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                    </Link>
                  </li>
                ))}
              </ul>
              <Link to="/countries" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-600 hover:text-teal-700">View all countries <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="bg-gradient-to-br from-teal-50 to-white border border-teal-100 rounded-2xl p-6 text-center">
              <h3 className="font-bold text-slate-900 mb-2">Ready to ship?</h3>
              <p className="text-sm text-slate-600 mb-4">Free doorstep pickup in India with guaranteed customs clearance.</p>
              <a href={contact.whatsapp} target="_blank" rel="noreferrer" className="w-full inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold px-5 py-3 rounded-lg transition-colors"><MessageCircle className="w-5 h-5" /> Book on WhatsApp</a>
            </div>
          </aside>
        </div>
      </article>

      {/* Related articles */}
      <section className="pb-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Related Articles</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {related.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="group bg-slate-50 rounded-2xl overflow-hidden border border-slate-100 hover:-translate-y-1 transition-transform">
                <div className="h-40 overflow-hidden"><img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                <div className="p-5">
                  <h3 className="font-bold text-slate-900 leading-snug group-hover:text-teal-600 transition-colors mb-2">{p.title}</h3>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-600">Read More <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
